import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './HomePage.module.css';
import { Box, Container, Grid, Typography } from '@mui/material';
import { DashboardCustomize, FileOpen } from '@mui/icons-material';
import { AzulBannerButton, AzulTrackingCardCarousel, AzulSkeleton } from '@the-ksquare-group/azul-ds';
import { docGeniusPalette } from 'styles/partials/colors';
import DocumentTable from './components/DocumentTable/DocumentTable';
import { useGetDocumentsQuery, useGetDocumentsSummaryQuery } from 'features/document/documentApi';
import { SearchBar } from 'components/SearchBar/SearchBar';
import { QueryBuilder } from 'utils/formatQueryParams';
import { useGetProfileQuery } from 'features/auth/authApi';
import { formatTextUpperCase } from 'utils/formatName';
import Pagination from '@mui/material/Pagination';
import { ReactComponent as ContractIcon } from 'assets/images/NewFileOptionsIcons/contract-option-icon.svg';
import { ReactComponent as DocumentIcon } from 'assets/images/NewFileOptionsIcons/document-option-icon.svg';
import { ReactComponent as QuoteIcon } from 'assets/images/NewFileOptionsIcons/quote-option-icon.svg';
import { ReactComponent as TemplateIcon } from 'assets/images/NewFileOptionsIcons/template-option-icon.svg';
import { setIsSalesforceAuth, setSalesforceId } from 'features/salesforce/salesforceSlice';
import { useAppDispatch } from 'app/hooks';
import { useGetSupportedDocTypesByOrgQuery } from 'features/organization/organizationApi';
import { setSupportedDocTypes } from 'features/organization/organizationSlice';
import { DocumentType } from 'features/document/documentSlice';

// TODO: modify azul skeleton to get height of parent container
// TODO: refactor code handling card selection after modifying carrousel component
interface CarrouselCard {
  name: string;
  status: string;
  sent: 'true' | 'false' | '';
  count: number | string;
}

enum CardsStatus {
  draft = 0,
  active = 1,
  sent = 2,
  signed = 3,
  voided = 4,
  inactive = 5,
}

const HomePage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchFile, setSearchFile] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [totalPages, setTotalPages] = useState(1);
  const [offset, setOffset] = useState(0);
  const { data: dataSummary, isFetching: isFetchingSummary } = useGetDocumentsSummaryQuery();

  const [selectedCard, setSelectedCard] = useState<CarrouselCard>({
    name: 'Drafts',
    status: 'draft',
    sent: '',
    count: 'loading',
  });

  const getCardStatus = () => {
    if (selectedCard.status === 'active') return ['active', 'created'];
    if (selectedCard.status === 'sent') return ['sent', 'sent_local'];
    if (selectedCard.status === 'signed') return ['completed', 'signed'];
    return [selectedCard.status];
  };

  const builder = new QueryBuilder();
  const queryString = builder
    .setQueryWhere({ field: 'name', operator: '$substring', value: searchFile })
    .setQueryWhere({
      field: 'status',
      operator: '$in',
      value: getCardStatus(),
    })
    .setQueryWhere({ field: 'sent', operator: '$eq', value: selectedCard.sent })
    .setQueryInclude({ field: 'recipients' })
    .setQueryInclude({ field: 'user' })
    .setQueryLimit({ value: itemsPerPage })
    .setQueryOffset({ value: offset })
    .build();
  const { data: dataDocuments, isFetching: isFetchingDocuments } = useGetDocumentsQuery(queryString);
  const { data: dataProfile, isFetching: isFetchingProfile } = useGetProfileQuery('profile');

  const isSalesforceAuth = dataProfile?.data.authType === 'salesforce';
  const { data: dataSupportedDocTypes, isFetching: isFetchingSupportedDocTypes } = useGetSupportedDocTypesByOrgQuery(
    undefined,
    {
      skip: !isSalesforceAuth,
    }
  );

  const getSupportedDocTypesObject = () => {
    if (dataSupportedDocTypes?.data.length) {
      return JSON.parse(dataSupportedDocTypes?.data[0].supportedDocumentTypes);
    }
    return {};
  };

  const supportedDocTypesObject = getSupportedDocTypesObject();
  const supportedDocTypes: string[] = supportedDocTypesObject?.types || [DocumentType.DOCUMENT];

  const cardItems = [
    {
      header: 'Drafts',
      counter: `${dataSummary?.data.draftDocuments}`,
      trackedItemLabel: 'file',
    },
    {
      header: 'Created',
      counter: `${dataSummary?.data.createdDocuments}`,
      trackedItemLabel: 'file',
    },
    {
      header: 'Sent',
      counter: `${dataSummary?.data.sentDocuments}`,
      trackedItemLabel: 'file',
    },
    {
      header: 'Completed',
      counter: `${dataSummary?.data.signedDocuments}`,
      trackedItemLabel: 'file',
    },
    {
      header: 'Voided',
      counter: `${dataSummary?.data.voidedDocuments}`,
      trackedItemLabel: 'file',
    },
    {
      header: 'Inactive',
      counter: `${dataSummary?.data.inactiveDocuments}`,
      trackedItemLabel: 'file',
    },
  ];

  const handleSelectedCard = (card: number[] | null) => {
    let count;
    let name;
    let sent: 'true' | 'false' | '' = '';
    let status = '';

    if (card === null) {
      count = Number(dataSummary?.data.totalDocuments);
      name = 'All';
    } else {
      const cardIndex = card[0];
      const { header, counter } = cardItems[cardIndex];
      count = Number(counter);
      name = header;
      sent = cardIndex === 2 ? 'true' : '';
      status = CardsStatus[cardIndex];
    }
    setSelectedCard({
      name: name,
      count: count,
      sent: sent,
      status: status,
    });
    setCurrentPage(1);
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
  };

  const handleSearch = (query: string) => {
    setSearchFile(query);
  };

  const getMenuItems = () => {
    const templateMenuItem = {
      icon: <TemplateIcon />,
      label: 'From Template Gallery',
      onClick: () => {
        navigate('/templates');
      },
    };
    const defaultMenuItems = [
      {
        icon: <DocumentIcon />,
        label: 'Document',
        onClick: () => {
          navigate(`/documents/create/document`);
        },
      },
      {
        icon: <DocumentIcon />,
        label: 'Preclosing',
        onClick: () => {
          navigate('/documents/create/preclosing');
        },
      },
      {
        icon: <ContractIcon />,
        label: 'Contract',
        onClick: () => {
          navigate('/documents/create/contract');
        },
      },
      {
        icon: <QuoteIcon />,
        label: 'Quote',
        onClick: () => {
          navigate('/documents/create/quote');
        },
      },
    ];

    if (isSalesforceAuth && supportedDocTypes) {
      const supportedDocTypesLowerCase = supportedDocTypes.map(type => type.toLowerCase());
      const filteredMenuItems = defaultMenuItems.filter(item =>
        supportedDocTypesLowerCase.includes(item.label.toLowerCase())
      );
      return [...filteredMenuItems, templateMenuItem];
    }

    return [defaultMenuItems[0], templateMenuItem];
  };

  const getTemplateMenuItems = () => {
    const defaultTemplateMenuItems = [
      {
        icon: <DocumentIcon />,
        label: 'Document',
        onClick: () => {
          navigate(`/templates/create/document`);
        },
      },
      {
        icon: <DocumentIcon />,
        label: 'Preclosing',
        onClick: () => {
          navigate(`/templates/create/preclosing`);
        },
      },
      {
        icon: <ContractIcon />,
        label: 'Contract',
        onClick: () => {
          navigate('/templates/create/contract');
        },
      },
      {
        icon: <QuoteIcon />,
        label: 'Quote',
        onClick: () => {
          navigate('/templates/create/quote');
        },
      },
    ];

    if (isSalesforceAuth && supportedDocTypes) {
      const supportedDocTypesLowerCase = supportedDocTypes.map(type => type.toLowerCase());
      const filteredMenuItems = defaultTemplateMenuItems.filter(item =>
        supportedDocTypesLowerCase.includes(item.label.toLowerCase())
      );
      return [...filteredMenuItems];
    }

    return [defaultTemplateMenuItems[0]];
  };

  useEffect(() => {
    if (dataSummary) {
      setSelectedCard({
        name: 'Drafts',
        status: 'draft',
        sent: '',
        count: dataSummary.data.draftDocuments,
      });
    }
  }, [isFetchingSummary, dataSummary]);

  useEffect(() => {
    const newOffset = (currentPage - 1) * itemsPerPage;
    setOffset(newOffset);
  }, [currentPage, itemsPerPage]);

  useEffect(() => {
    if (dataDocuments) {
      const totalDocumentsCount = dataDocuments.count || 0;
      const total = Math.ceil(totalDocumentsCount / itemsPerPage);
      setTotalPages(total);
    }
  }, [dataDocuments]);

  useEffect(() => {
    dispatch(setIsSalesforceAuth(isSalesforceAuth));
    if (dataProfile) {
      dispatch(setSalesforceId(dataProfile.data.salesforceId));
    }
  }, [dataProfile]);

  useEffect(() => {
    if (supportedDocTypes) {
      dispatch(setSupportedDocTypes(supportedDocTypes));
    }
  }, [dataSupportedDocTypes]);

  return (
    <Container>
      {!isFetchingProfile && (
        <Typography
          variant='h4'
          gutterBottom
          color={docGeniusPalette.primaryBlue}
          align='left'
          className={styles.username}
          sx={{ fontSize: '24px', fontWeight: 500 }} // 24px
        >
          Hello, {formatTextUpperCase(dataProfile?.data?.name)}!
        </Typography>
      )}

      <Typography
        variant='h3'
        gutterBottom
        color={docGeniusPalette.primaryBlue}
        align='left'
        className={styles.welcomeText}
        sx={{ fontSize: '40px', fontWeight: 500 }} // 40px
      >
        Welcome to DocGenius!
      </Typography>

      {isFetchingSupportedDocTypes ? (
        <Box marginTop={'2rem'}>
          <AzulSkeleton height={270} variant='rounded' />
        </Box>
      ) : (
        <Box
          className={styles.banner}
          display='flex'
          flexDirection='column'
          alignItems='center'
          justifyContent='center'
          sx={{ padding: '16px', paddingTop: '48px', paddingBottom: '48px' }}
        >
          <Typography
            variant='h4'
            gutterBottom
            color={docGeniusPalette.primaryBlue}
            sx={{ fontSize: '48px', textAlign: 'center', fontWeight: 500 }} // 48px
          >
            DocGenius
          </Typography>
          {/* TODO: CHANGE AZULBANNER BUTTON, SINCE WE GET ERROR ON CONSOLE ALSO HAPPENS WITH AZULBUTTON*/}
          <Box display='flex' justifyContent='center' width='100%' className={styles.iconContainer}>
            <Box display='flex' flexDirection='column' alignItems='center' className={styles.iconBox}>
              <AzulBannerButton
                buttonText={'New File'}
                icon={<FileOpen />}
                variant={'circular'}
                items={getMenuItems()}
              />
            </Box>
            <Box display='flex' flexDirection='column' alignItems='center' className={styles.iconBox}>
              <AzulBannerButton
                buttonText={'New Template'}
                icon={<DashboardCustomize />}
                variant={'circular'}
                items={getTemplateMenuItems()}
              />
            </Box>
          </Box>
        </Box>
      )}
      <Box>
        <Box display='flex' justifyContent='space-between' padding='65px 0 28px 0'>
          <Typography
            sx={{
              color: docGeniusPalette.primaryBlue,
              fontFamily: 'Rubik',
              fontSize: '32px',
              fontWeight: 500,
              lineHeight: '37.92px',
            }}
          >
            Tracking Overview
          </Typography>
        </Box>
        <Box className={styles.boxShadow}>
          {!isFetchingSummary && (
            <Box>
              <AzulTrackingCardCarousel
                autoPlay={false}
                allowMultipleSelection={false}
                items={cardItems}
                onCardSelect={handleSelectedCard}
              />
            </Box>
          )}

          <Grid container className={styles.container}>
            <Grid item container xs={12} mb='32px'>
              <Grid item xs={6}>
                <Grid direction='row' alignItems='flex-end' container gap={2}>
                  <Typography
                    variant='h3'
                    color={docGeniusPalette.primaryBlue}
                    sx={{ fontSize: '24px', fontWeight: 500 }}
                  >
                    {selectedCard.name}
                  </Typography>
                  <Typography
                    variant='caption'
                    color={docGeniusPalette.primaryBlue}
                    sx={{ fontSize: '16px', fontWeight: 400 }}
                  >
                    {`${selectedCard.count} ${Number(selectedCard.count) === 1 ? 'file' : 'files'}`}
                  </Typography>
                </Grid>
              </Grid>
              <Grid item xs={6}>
                <SearchBar placeholder='Search file' value={searchFile} onSearch={handleSearch} />
              </Grid>
            </Grid>
          </Grid>

          <Box className={styles.tableContainer}>
            {isFetchingDocuments ? (
              <Box padding={'24px'}>
                <AzulSkeleton height={300} />
              </Box>
            ) : (
              <>
                <DocumentTable data={dataDocuments !== undefined && dataDocuments.data ? dataDocuments.data : []} />
              </>
            )}
          </Box>
          {totalPages > 0 && (
            <Box className={styles.paginationContainer}>
              <Pagination
                className='pagination'
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color='primary'
              />
            </Box>
          )}
        </Box>
      </Box>
    </Container>
  );
};

export default HomePage;
